// Default page count for scenes with no page count
export const DEFAULT_PAGE_COUNT = 1.0;

// ─── metric selector (D·E·R) ──────────────────────────────────────
// The der_result transform emits the SAME score keys at every grain
// (script/act/sequence/scene), so each metric maps to one key across all
// node types — unlike the legacy PCRH metrics which differed per grain.
// Three lenses + the twelve axes (Design: contract/pressure/development/
// movement/consequence · Execution: economy/assembly/clarity · Read:
// freshness/unpredictability/charge/grip).
export const METRIC_LABELS = {
  overall:        "Triage",
  de:             "Design + Execution",
  design:         "Design",
  execution:      "Execution",
  read:           "Involvement",   // was "Read" (owner, 2026-10-14) — the KEY stays `read`
  contract:       "Contract",
  pressure:       "Pressure",
  development:    "Development",
  movement:       "Movement",
  consequence:    "Consequence",
  economy:        "Economy",
  assembly:       "Assembly",
  clarity:        "Clarity",
  freshness:      "Freshness",
  unpredictability:"Unpredictability",
  charge:         "Charge",
  grip:           "Grip"
};

/*
   Map <metric> → score key in the scores objects. der_result uses one key
   per metric at all grains, so movie/act/sequence/scene share it. 'overall'
   falls back to execution (handled in prepareChartData via obj.execution).
*/
const _allGrains = key => ({ movie: key, act: key, sequence: key, scene: key });
export const METRIC_KEYS = {
  overall:          _allGrains("overallScore"),
  de:               _allGrains("overallScore"),  // legacy default look (design border + execution fill)
  design:           _allGrains("design"),
  execution:        _allGrains("execution"),
  read:             _allGrains("read"),
  contract:         _allGrains("contract"),
  pressure:         _allGrains("pressure"),
  development:      _allGrains("development"),
  movement:         _allGrains("movement"),
  consequence:      _allGrains("consequence"),
  economy:          _allGrains("economy"),
  assembly:         _allGrains("assembly"),
  clarity:          _allGrains("clarity"),
  freshness:        _allGrains("freshness"),
  unpredictability: _allGrains("unpredictability"),
  charge:           _allGrains("charge"),
  grip:             _allGrains("grip")
};


// Fallback for simple metric lookup (backwards compatibility)
export const SIMPLE_METRIC_TO_CATEGORY = {
  // Act metrics (keeping your original mapping as fallback)
  pacing: "Technical & Readability",
  emotionalImpact: "Emotional & Engagement", 
  originality: "Originality & Voice",
  stakesHandling: "Narrative Structure",
  character: "Character & Thematic Arcs"
};

// Updated color mapping based on ratings (0-3) with red to green spectrum
export const ratingColors = {
  0: "#FF3B30",
  1: "#FF9500", 
  2: "#7ED321",
  3: "#34C759"
};

// The GRADE gradient (single-axis + Design&Execution colouring). Pastel GREEN → PEACH → RED, matching
// the pleasant prod look — NO yellow/amber/olive in the middle (which read "ugly"); the warm mid is
// peach, and it steps straight to pale green. Fully OPAQUE (no rgba alpha) so the outer scene ring
// renders identically to the inner rings — soft, not translucent.
export const gradeColors = {
  1:  "#E25C53",  // red (needs work)
  2:  "#EC7669",
  3:  "#F0917F",  // salmon
  4:  "#F5AD95",  // peach
  5:  "#F9C8AB",  // light peach (functional)
  6:  "#FBDABE",  // pale peach
  7:  "#CDE8B4",  // pale green (getting there)
  8:  "#A8DB94",
  9:  "#83CC78",
  10: "#5DB85F",  // green (strong)
};

export const borderRatingColors = {
  0: "#CC2F26",
  1: "#CC7700",
  2: "#65A91B", 
  3: "#2AA147"
};

export const ratingText = {
  0: "Pass",
  1: "Consider",
  2: "Recommend",
  3: "Highly Recommend"
};

export const BORDER_START = "#111111";
export const BORDER_ROOT = "#999999";
export const BORDER_SCENE = "#333333";
export const BORDER_WIDTH = 5;

export const badge = r => ["❌", "⚠️", "👍", "✅"][r] ?? "";
export const wrap  = str => str?.length > 50 ? str.slice(0, 50) + "…" : str;

/* ── Score display ────────────────────────────────────────────────────────────
   "4/10" reads faster than "4.0/10" (owner, 2026-08-30). Axis scores are whole
   numbers from the grader, so the decimal was pure noise on every one of them;
   lens and grain scores are MEANS and keep their decimal, because 3.2 is not 3.
   Do NOT reach for toFixed(1) directly — that is what produced "4.0" in eight
   files. This existed twice already as a local const (TableView, CraftReadTab);
   both now import it. */
export const fmtScore = (v) => {
  const n = parseFloat(v);
  if (!Number.isFinite(n)) return '';
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
};


/* The three lenses and the axes under each — the framework's own shape, in canonical order.
   Lives here because BOTH NodePanel (the per-unit rail) and Icicle (the Axes tab) group by it, and
   two copies of a taxonomy drift. The `key`s match the lens score keys the server emits alongside
   the twelve axes (`scores.design` / `.execution` / `.read`), and the axis order is the order the
   framework defines them in — deliberately NOT a score order. See the note in AxesOverview. */
export const LENSES = [
  { key: 'design',    label: 'Design',    axes: ['contract', 'pressure', 'development', 'movement', 'consequence'] },
  { key: 'execution', label: 'Execution', axes: ['economy', 'assembly', 'clarity'] },
  { key: 'read',      label: 'Involvement',      axes: ['freshness', 'unpredictability', 'charge', 'grip'] },
];
/* Nine plain-English lens verdicts — the teaching payload. A lens score on its own is a number a
   reader has no scale for; "Built, but leaking power" is the thing they actually learn. Keyed
   [lens][solid|soft|broken], the statuses verdictModel.lensStatus() returns. Shared by the per-unit
   rail (NodePanel) and the Axes tab (Icicle) for the same reason LENSES is. */
export const LENS_HEADLINE = {
  design:    { solid: 'Built and working', soft: 'Built, but leaking power', broken: 'Not holding yet' },
  execution: { solid: 'Plays cleanly',     soft: 'Plays, but not crisp',     broken: 'Not playing on the page' },
  read:      { solid: 'Grips and lands',   soft: 'Steady, not yet gripping', broken: 'Not landing yet' },
};

/* ── Strip / timeline score scale ─────────────────────────────────────────────
   A DISTINCT 10-step scale for axis and lens views, and the remap that gets there
   from `gradeColors`.

   Why it exists: `gradeColors` above carries two near-identical peach shades per
   band, so adjacent scores (3 & 4, 5 & 6) look the same and the worst score does
   not pop. This scale gives every score its own hue and lightness, red-anchored at
   the bottom so the weakest units read first.

   ⚠️ Lived in Icicle.js as three module-level constants until 2026-09-01, which
   meant the LR timeline had the fix and the landing-page snapshot did not — same
   metric, same data, visibly paler colours, because both call the shared
   prepareChartData() and only one of them remapped afterwards (owner: "why are the
   colors washed out compared to LR?"). Import from here; do not re-declare.

   ⚠️ TRIAGE MUST PASS THROUGH UNCHANGED. softFill() only substitutes values that
   are literally in gradeColors, so BAND_FILL's vivid band colours — including the
   raspberry #E11D48 — survive untouched. Widen the map and the default view turns
   rose. */
export const SCORE_SCALE = ['#B41527', '#D93030', '#E85D2F', '#F0883C', '#F4B63C',
                            '#E9D23E', '#BCD84E', '#8CC751', '#5BB04A', '#2F9040'];
const GRADE_HEX = ['#E25C53', '#EC7669', '#F0917F', '#F5AD95', '#F9C8AB',
                   '#FBDABE', '#CDE8B4', '#A8DB94', '#83CC78', '#5DB85F'];
export const SCORE_REMAP = Object.fromEntries(GRADE_HEX.map((h, i) => [h, SCORE_SCALE[i]]));
/* gradeColors fill -> distinct scale. Anything else (band colours, the not-owed
   slate #eef2f5) passes through as-is. */
export const softFill = (c) => (c && SCORE_REMAP[c.toUpperCase()]) || c || '#e2e8f0';
/* 0-10 score -> the distinct scale, for callers holding a number not a colour. */
export const scoreHex = (v) => SCORE_SCALE[Math.max(0, Math.min(9, Math.round(v) - 1))];
